<script setup>
  import FroalaEditor from "froala-editor";
  import { inject, onMounted, ref, watch } from "vue";
  import { v4 as uuidv4 } from "uuid";
  import GenexMathView from "@/components/froala/GenexMathView.vue";
  import { useBuefy } from "@/compositions/utils/useBuefy";
  import { useVueApp } from "@/compositions/useVueApp";
  import MathLiveKeyboard from "@/components/froala/MathLiveKeyboard.vue";

  const emit = defineEmits(["input"]);
  const uploading = inject("uploading");

  const props = defineProps({
    value: {
      type: String,
      default: "",
    },
    label: {
      type: String,
      default: null,
    },
    config: {
      type: Object,
      required: false,
      default: () => ({}),
    },
    hideMathView: {
      type: Boolean,
      default: false,
    },
    model: {
      type: String,
      default: "Modules\\AssessmentQuestion\\Entities\\AssessmentQuestion",
    },
    modelId: {
      type: Number,
      required: true,
    },
    placeholder: {
      type: String,
      default: "Write Something Here...",
    },
    enableCharacterLimit: {
      type: Boolean,
      default: false,
    },
    maxCharacters: {
      type: Number,
      default: 9999,
    },
  });

  const valueCopy = ref(props.value);
  watch(
    () => props.value,
    value => {
      valueCopy.value = value;
    },
  );
  watch(valueCopy, value => {
    emit("input", value);
  });

  const app = useVueApp();

  const editor = ref(null);

  const checkUpload = () => {
    uploading.value = true;
    const watchTemp = watch(valueCopy, () => {
      uploading.value = false;
      watchTemp();
    });
  };

  const $buefy = useBuefy();

  const showMathLiveKeyboard = () => {
    // Save the current selection (cursor position)
    const savedSelection = editor.value.getEditor().selection.save();

    $buefy.modal.open({
      parent: app,
      component: MathLiveKeyboard,
      hasModalCard: true,
      trapFocus: true,
      canCancel: ["x"],
      events: {
        add: async latex => {
          // Restore the selection (cursor position)
          if (savedSelection) {
            editor.value.getEditor().selection.restore();
          }

          // Insert latex at cursor position
          editor.value.getEditor().html.insert(latex);
          valueCopy.value = editor.value.getEditor().html.get();
        },
      },
    });
  };

  const uuid = uuidv4();

  onMounted(() => {
    FroalaEditor.DefineIconTemplate("material_design", '<i class="mdi mdi-[NAME]" style="font-size: 21px;"></i>');
    FroalaEditor.DefineIcon("functionIcon", { NAME: "function", template: "material_design" });

    FroalaEditor.RegisterCommand(`fx${uuid}`, {
      title: "Scientific Notation",
      icon: "functionIcon",
      focus: false,
      undo: false,
      callback: function () {
        showMathLiveKeyboard();
      },
    });
    if (props.enableCharacterLimit && props.maxCharacters) {
      tempConfig.charCounterMax = props.maxCharacters;
    }
  });

  const tempConfig = {
    key: process.env.VUE_APP_FROALA_API_KEY,
    pluginsEnabled: [
      "image",
      "file",
      "video",
      "align",
      "draggable",
      "fontFamily",
      "fontSize",
      "lists",
      "imageTUI",
      "charCounter",
      "url",
      "lineBreaker",
      "lineHeight",
      "help",
      "fullscreen",
      "link",
    ],
    imageInsertButtons: ["imageUpload", "imageByURL"],
    imageAllowedTypes: ["jpeg", "jpg", "png", "svg+xml"],
    imageUploadRemoteUrls: true,
    imageUploadParams: {
      model_id: props.modelId,
      model: props.model,
      is_public: 1,
      return_link_only: 1,
    },
    imageUploadURL: `${process.env.MIX_API_V4_URL}media`,
    videoInsertButtons: ["videoUpload"],
    videoAllowedTypes: ["mp4", "webm"],
    videoUploadRemoteUrls: true,
    videoUploadURL: `${process.env.MIX_API_V4_URL}media`,
    videoUploadParams: {
      model_id: props.modelId,
      model: props.model,
      is_public: 1,
      return_link_only: 1,
    },
    events: {
      "image.beforeUpload": checkUpload,
      "file.beforeUpload": checkUpload,
      "video.beforeUpload": checkUpload,
      "file.inserted": function ($file) {
        if ($file.attr("href")) {
          $file.attr("target", "_blank");
        }
      },
      "image.error": function (error) {
        if (error.code === 5) {
          alert("The image size exceeds the maximum allowed size of 10MB.");
        } else {
          alert("An error occurred while uploading the image.");
        }
      },
      "video.error": function (error) {
        if (error.code === 5) {
          alert("The video size exceeds the maximum allowed size of 10MB.");
        } else {
          alert("An error occurred while uploading the video.");
        }
      },
      "file.error": function (error) {
        if (error.code === 5) {
          alert("The file size exceeds the maximum allowed size of 10MB.");
        } else {
          alert("An error occurred while uploading the file.");
        }
      },
    },
    fileAllowedTypes: ["application/pdf"],
    fileUploadURL: `${process.env.MIX_API_V4_URL}media`,
    fileUploadParams: {
      model_id: props.modelId,
      model: props.model,
      is_public: 1,
      return_link_only: 1,
    },
    linkAlwaysBlank: true,
    fileUploadRemoteUrls: true,
    requestHeaders: {
      Authorization: `Bearer ${app.$cookies.get("access_token")}`,
    },
    videoMaxSize: 10 * 1024 * 1024,
    fileMaxSize: 10 * 1024 * 1024,
    imageMaxSize: 10 * 1024 * 1024,
    linkConvertEmailAddress: true,
    linkInsertButtons: ["linkBack"],
    toolbarButtons: {
      moreText: {
        buttons: [
          "bold",
          "italic",
          "underline",
          "strikeThrough",
          "subscript",
          "superscript",
          "fontFamily",
          "fontSize",
          "textColor",
          "backgroundColor",
          "clearFormatting",
        ],
      },
      moreParagraph: {
        buttons: [
          "alignLeft",
          "alignCenter",
          "formatOLSimple",
          "alignRight",
          "alignJustify",
          "formatOL",
          "formatUL",
          "lineHeight",
          "indent",
          "outdent",
        ],
      },
      moreRich: {
        buttons: ["insertLink", "insertImage", "insertVideo", "uploadFile", "insertHR"],
      },
      moreMisc: {
        buttons: ["undo", "redo", `fx${uuid}`, "fullscreen", "getPDF", "selectAll", "help"],
      },
    },
    attribution: false,
    placeholderText: props.placeholder,
  };
</script>

<template>
  <div>
    <b-field message="Use LaTeX syntax for mathematical expressions." :label="label">
      <froala
        @fxEvent="showMathLiveKeyboard"
        ref="editor"
        :tag="'textarea'"
        :config="tempConfig"
        :value="valueCopy"
        @update:value="valueCopy = $event"></froala>
    </b-field>
    <GenexMathView :content="valueCopy" v-if="!hideMathView" hide-if-no-math @fxEvent="showMathLiveKeyboard" />
    <slot name="footer"></slot>
  </div>
</template>

<style scoped></style>
